import { useContext } from 'react';
import { PersonalDataProps, REGEXP_NAME_SURNAME } from './PersonalData.model';
import { useTranslation } from 'react-i18next';
import styles from './PersonalData.module.css';
import { Text } from '../../../../foundations';
import { InputRadio, InputRadioGroup } from '../../../../forms';
import { useFormContext } from 'react-hook-form';
import { genderOptions, getMinDate, validateTwoSurnames } from './PersonalData.utils';
import { FormDataApis, SelectOptions } from '../../ApisForm.model';
import {
	InputTextCleaner,
	getNestedErrorForm,
	ControllerInputSelect,
	ControllerInputDateSelect
} from '../../../../forms/react-hook-form';
import { ApisFormContext } from '../../ApisForm.utils';
import { PersonalDataFields } from '../../ApisForm.constants';
import { ErrorBoundaryProvider } from '../../../../../providers/ErrorBoundaryProvider';

/**
 * Section form Apis personal data
 *
 * @param {PersonalDataProps} props
 * @returns {TSX.Element}
 */
export const PersonalData = ({ typePassenger }: PersonalDataProps) => {
	const { t } = useTranslation(['personal-data', 'common']);
	const {
		register,
		formState: { errors }
	} = useFormContext<FormDataApis>();
	const { listOptions, isFormDisabled = false } = useContext(ApisFormContext);
	const isInfant = typePassenger === 'INFANT';
	const modelInfant = isInfant ? 'infant.' : '';

	return (
		<ErrorBoundaryProvider>
			<div className={styles.box_container}>
				<Text as="h4" font="iberia-headline" className={styles.box_title}>
					{t('TITLE')}
				</Text>
				<div className={styles.box_content}>
					{/* INPUT Name */}
					<div className={styles.box_input}>
						<InputTextCleaner
							{...register(`${modelInfant}${PersonalDataFields.NAME}`, { required: t('NAME_REQUIRED_ERROR') })}
							name={`${modelInfant}${PersonalDataFields.NAME}`}
							label={t('NAME')}
							regexCleaner={REGEXP_NAME_SURNAME}
							className={styles.box_input_text}
							labelStyles={styles.labelStyles}
							disabled={isFormDisabled}
						/>
					</div>
					<div className={styles.box_input}>
						{/* INPUT Surname */}
						<InputTextCleaner
							{...register(`${modelInfant}${PersonalDataFields.SURNAME}`, {
								required: t('SURNAME_REQUIRED_ERROR'),
								validate: {
									nationality: (v, formValues) =>
										validateTwoSurnames(v, formValues, isInfant) ? t('SURNAME_DNI_VALIDATOR_ERROR') : undefined
								}
							})}
							name={`${modelInfant}${PersonalDataFields.SURNAME}`}
							label={t('SURNAME')}
							regexCleaner={REGEXP_NAME_SURNAME}
							className={styles.box_input_text}
							labelStyles={styles.labelStyles}
							disabled={isFormDisabled}
						/>
					</div>
					<div className={styles.box_input}>
						{/* select birth date */}
						<ControllerInputDateSelect
							name={`${modelInfant}${PersonalDataFields.BIRTHDATE}`}
							label={t('BIRTHDATE')}
							rules={{ required: t('BIRTHDAY_REQUIRED_ERROR') }}
							minDate={getMinDate(typePassenger)}
							disabled={isFormDisabled}
						/>
					</div>
					<div className={styles.box_input}>
						{/* select nationality */}
						<ControllerInputSelect
							name={`${modelInfant}${PersonalDataFields.NATIONALITY}`}
							label={t('NATIONALITY')}
							options={listOptions.nationalities as SelectOptions[]}
							rules={{ required: t('NATIONALITY_REQUIRED_ERROR') }}
							triggersName={`${modelInfant}${PersonalDataFields.NATIONALITY}`}
							disabled={isFormDisabled}
						/>
					</div>
					<div className={styles.box_input}>
						{/* select home country */}
						<ControllerInputSelect
							name={`${modelInfant}${PersonalDataFields.COUNTRY}`}
							label={t('HOME_COUNTRY')}
							options={listOptions.countries as SelectOptions[]}
							rules={{ required: t('HOME_COUNTRY_REQUIRED_ERROR') }}
							disabled={isFormDisabled}
						/>
					</div>

					{/* gender */}
					<div className={styles.box_input_radio}>
						<Text as="h4" font="verdana" className={styles.box_secundary_title}>
							{t('GENDER')}
						</Text>

						<InputRadioGroup
							{...{
								ariaLabel: 'GENDER',
								direction: 'horizontal',
								disabled: isFormDisabled,
								errorMessage: getNestedErrorForm(errors, `${modelInfant}${PersonalDataFields.GENDER}`)?.message
							}}
						>
							{genderOptions.map(({ label, value }) => {
								return (
									<InputRadio
										key={label}
										label={t(label)}
										stylesRadio={styles.box_input_option}
										data-testid={`${modelInfant}${PersonalDataFields.GENDER}-${value}`}
										value={value}
										disabled={isFormDisabled}
										{...register(`${modelInfant}${PersonalDataFields.GENDER}`, {
											required: t('GENERAL_MANDATORY_FIELDS_TEXT')
										})}
									/>
								);
							})}
						</InputRadioGroup>
					</div>
				</div>
			</div>
		</ErrorBoundaryProvider>
	);
};
PersonalData.displayName = 'PersonalData';
